import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositions,
    DynamicFormTextFieldLabelPositionsLeft,
    DynamicFormTextFieldLabelPositionsNone,
    DynamicFormTextFieldLabelPositionsTop,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    labelPosition = "labelPosition",
    labelColor = "labelColor",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    enableMinLength = "enableMinLength",
    minLength = "minLength",
    enableMaxLength = "enableMaxLength",
    maxLength = "maxLength",
    validateAsPhoneNumber = "validateAsPhoneNumber",
    validateAsEmailAddress = "validateAsEmailAddress",
}

interface OwnProps {
    labelColorWhite?: boolean;
    isDisabled?: boolean;
    fields: {
        [fields.key]: string;
        [fields.labelPosition]: DynamicFormTextFieldLabelPositions;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.enableMinLength]: boolean;
        [fields.minLength]: number;
        [fields.enableMaxLength]: boolean;
        [fields.maxLength]: number;
        [fields.validateAsPhoneNumber]: boolean;
        [fields.validateAsEmailAddress]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormTextFieldStyles {
    wrapper?: InjectableCss;
    textField: TextFieldPresentationProps;
}

export const dynamicFormTextFieldStyles: DynamicFormTextFieldStyles = {
    wrapper: {
        css: css`
            width: 100%;
            align-items: center;
        `,
    },
    textField: {},
};

const styles = dynamicFormTextFieldStyles;

const DynamicFormTextField: React.FC<Props> = ({
    fields,
    dynamicFormData,
    setDynamicFormFieldValue,
    labelColorWhite,
    isDisabled,
}) => {
    const {
        key,
        labelPosition,
        label,
        hintText,
        isRequired,
        enableMinLength,
        minLength,
        enableMaxLength,
        maxLength,
        validateAsEmailAddress,
        validateAsPhoneNumber,
    } = fields;

    const defaultTextFieldValue = "";

    const [textFieldValue, setTextFieldValue] = useState(defaultTextFieldValue);
    const [textFieldErrorMessage, setTextFieldErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    const dynamicFormContext = useContext(DynamicFormContext);

    useEffect(() => {
        initializeTextFieldWidget();
    }, [dynamicFormData?.forms]);

    useEffect(() => {
        const { isValid, errorMessage } = ValidateTextFieldValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: labelPosition === DynamicFormTextFieldLabelPositionsNone ? key : label,
                isRequired,
                value: textFieldValue,
                type: "DynamicFormTextField",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    }, [textFieldValue]);

    useEffect(() => {
        if (textFieldValue !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setTextFieldValue(defaultTextFieldValue);
            } else {
                setTextFieldValue(dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    const initializeTextFieldWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = ValidateTextFieldValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: labelPosition === DynamicFormTextFieldLabelPositionsNone ? key : label,
                isRequired,
                value: defaultTextFieldValue,
                type: "DynamicFormTextField",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    };

    const ValidateTextFieldValue = () => {
        let errorMessage =
            isRequired && textFieldValue?.length < 1 ? (siteMessage("Field_Required", label) as string) : "";

        if (errorMessage.length < 1) {
            errorMessage =
                enableMinLength && textFieldValue?.length < minLength
                    ? (siteMessage("DynamicForms_Field_Too_Short", label, minLength.toString()) as string)
                    : "";
        }

        if (errorMessage.length < 1) {
            errorMessage =
                enableMaxLength && textFieldValue?.length > maxLength
                    ? (siteMessage("Field_Too_Long", label, maxLength.toString()) as string)
                    : "";
        }

        if (errorMessage.length < 1 && textFieldValue && textFieldValue.trim().length > 0 && validateAsEmailAddress) {
            errorMessage = !DynamicFormEmailRegExp.test(textFieldValue)
                ? (siteMessage("Field_Invalid", label) as string)
                : "";
        }

        if (errorMessage.length < 1 && textFieldValue && textFieldValue.trim().length > 0 && validateAsPhoneNumber) {
            const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;
            errorMessage = !phoneRegex.test(textFieldValue) ? (siteMessage("Field_Invalid", label) as string) : "";
        }

        setTextFieldErrorMessage(errorMessage);

        return {
            isValid: !errorMessage,
            errorMessage,
        };
    };

    const textFieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setTextFieldValue(newValue);
    };

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    type FinalLabelPosition = "left" | "top" | undefined;
    const FinalLabelPositionLeft: FinalLabelPosition = "left";
    const FinalLabelPositionTop: FinalLabelPosition = "top";
    const FinalLabelPositionUndefined: FinalLabelPosition = undefined;

    const determineFinalLabelPosition = () => {
        let returnValue: FinalLabelPosition = FinalLabelPositionUndefined;
        switch (labelPosition) {
            case DynamicFormTextFieldLabelPositionsLeft:
                returnValue = FinalLabelPositionLeft;
                break;
            case DynamicFormTextFieldLabelPositionsTop:
                returnValue = FinalLabelPositionTop;
                break;
            case DynamicFormTextFieldLabelPositionsNone:
                returnValue = FinalLabelPositionUndefined;
                break;
            default:
                returnValue = FinalLabelPositionTop;
                break;
        }
        return returnValue;
    };

    const finalLabelPosition: FinalLabelPosition = determineFinalLabelPosition();

    let finalTextFieldStyles: TextFieldPresentationProps = styles.textField;
    if (labelColorWhite) {
        finalTextFieldStyles = finalTextFieldStyles && {
            labelProps: {
                color: "common.accent",
            },
        };
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                {...finalTextFieldStyles}
                labelPosition={finalLabelPosition}
                label={labelPosition !== DynamicFormTextFieldLabelPositionsNone ? label : undefined}
                required={isRequired}
                placeholder={hintText}
                value={textFieldValue}
                disabled={isDisabled}
                error={getDisplayValidationErrors() && textFieldErrorMessage}
                onChange={textFieldChangeHandler}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormTextField);
